import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useApi } from '../../lib/api';
import { Chip } from '../../components/Atoms';
import { modalInputStyle } from './Modal';

/**
 * PlantPickerInput — autocomplete dla rośliny z bazy 1100+.
 *
 * Props:
 *   value           - aktualny plant_name (string)
 *   onChange(name, plant?) - callback. plant to obiekt {id, days_to_harvest, ...} jeśli pasuje z bazy
 *   plantedDate     - data sadzenia (YYYY-MM-DD) - do auto-obliczenia harvestDate
 *   onHarvestDateCalculated(date) - opcjonalny callback z obliczoną expected_harvest_date
 *   placeholder
 *   id (opcjonalny)
 *
 * Logika:
 *   - User pisze → pokazuje 12 najlepszych dopasowań z bazy
 *   - User klika sugestię → wypełnia value + emituje plant + harvestDate
 *   - User pisze własną nazwę (nie ma w bazie) → wartość przekazana, plant=null, harvestDate=null
 *   - Search po: name, display_name, latin_name
 */

const CATEGORY_LABEL = {
  vegetable: 'Warzywo',
  fruit: 'Owoc',
  fruit_tree: 'Drzewo owocowe',
  fruit_bush: 'Krzew owocowy',
  herb: 'Zioło',
  flower_annual: 'Kwiat jednoroczny',
  flower_perennial: 'Bylina',
  flower_bulb: 'Cebulowy',
  shrub: 'Krzew',
  tree: 'Drzewo',
  rose: 'Róża',
  climber: 'Pnącze',
  grass: 'Trawa',
  groundcover: 'Okrywowa',
  succulent: 'Sukulent',
  fern: 'Paproć',
  evergreen: 'Iglak',
};

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function scoreMatch(plant, q) {
  const lower = q.toLowerCase();
  const name = (plant.name || '').toLowerCase();
  const display = (plant.display_name || '').toLowerCase();
  const latin = (plant.latin_name || '').toLowerCase();
  // Dokładny match → najwyższy priorytet
  if (display === lower || name === lower) return 100;
  // Start
  if (display.startsWith(lower) || name.startsWith(lower)) return 80;
  // Slowo zaczyna się od query
  if (display.split(' ').some(w => w.startsWith(lower))) return 60;
  if (name.split(' ').some(w => w.startsWith(lower))) return 60;
  // Zawiera
  if (display.includes(lower) || name.includes(lower)) return 40;
  if (latin.includes(lower)) return 30;
  return 0;
}

export default function PlantPickerInput({
  value,
  onChange,
  plantedDate,
  onHarvestDateCalculated,
  placeholder = 'Zacznij wpisywać — np. pomidor, róża, mięta…',
  id,
}) {
  // Cachowane raz na mount (1100+ roślin)
  const { data } = useApi('/api/plants?limit=1500');
  const allPlants = useMemo(() => data?.data || [], [data]);

  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [matchedPlant, setMatchedPlant] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const suggestions = useMemo(() => {
    if (!value || value.length < 1) return [];
    const scored = allPlants
      .map(p => ({ p, s: scoreMatch(p, value) }))
      .filter(({ s }) => s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 12)
      .map(({ p }) => p);
    return scored;
  }, [value, allPlants]);

  // Wykrywa exact match (case-insensitive)
  useEffect(() => {
    if (!value || allPlants.length === 0) {
      setMatchedPlant(null);
      return;
    }
    const lower = value.toLowerCase();
    const hit = allPlants.find(p =>
      (p.display_name || '').toLowerCase() === lower ||
      (p.name || '').toLowerCase() === lower
    );
    setMatchedPlant(hit || null);
  }, [value, allPlants]);

  // Auto-oblicza harvest date gdy dopasowanie + planted_date
  useEffect(() => {
    if (matchedPlant && matchedPlant.days_to_harvest > 0 && plantedDate) {
      const date = addDays(plantedDate, matchedPlant.days_to_harvest);
      onHarvestDateCalculated?.(date, matchedPlant.days_to_harvest);
    } else {
      onHarvestDateCalculated?.(null, null);
    }
  }, [matchedPlant, plantedDate, onHarvestDateCalculated]);

  // Klik poza zamyka
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [open]);

  const pickPlant = (plant) => {
    const name = plant.display_name || plant.name;
    onChange(name, plant);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pickPlant(suggestions[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value || ''}
        onChange={e => {
          onChange(e.target.value, null);
          setOpen(true);
          setHighlighted(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        style={modalInputStyle}
      />

      {/* Wskazówka czy pasuje z bazy */}
      {value && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          {matchedPlant ? (
            <>
              <Chip tone="sage">✓ z bazy</Chip>
              {matchedPlant.category && CATEGORY_LABEL[matchedPlant.category] && (
                <span style={{
                  fontSize: 11, color: 'var(--t-text-mute)',
                  fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                }}>{CATEGORY_LABEL[matchedPlant.category]}</span>
              )}
              {matchedPlant.days_to_harvest > 0 && (
                <span style={{
                  fontSize: 11, color: 'var(--t-amber)',
                  fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                }}>~ {matchedPlant.days_to_harvest} dni do zbioru</span>
              )}
            </>
          ) : (
            <Chip tone="dim">własna nazwa</Chip>
          )}
        </div>
      )}

      {/* Dropdown z sugestiami */}
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)', left: 0, right: 0,
          maxHeight: 320,
          overflow: 'auto',
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line)',
          borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,.5)',
          zIndex: 10,
        }}>
          {suggestions.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pickPlant(p)}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                width: '100%', textAlign: 'left',
                padding: '10px 14px',
                background: i === highlighted ? 'rgba(168, 196, 158, 0.10)' : 'transparent',
                border: 'none',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--t-line)' : 'none',
                cursor: 'pointer',
                color: 'var(--t-text)',
                fontFamily: 'var(--t-sans)', fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  letterSpacing: '-.01em',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{p.display_name || p.name}</div>
                {p.latin_name && (
                  <div style={{
                    fontSize: 11, color: 'var(--t-text-mute)',
                    fontStyle: 'italic', marginTop: 2,
                  }}>{p.latin_name}</div>
                )}
              </div>
              {p.category && CATEGORY_LABEL[p.category] && (
                <span style={{
                  fontFamily: 'var(--t-mono)', fontSize: 10,
                  letterSpacing: '.16em', textTransform: 'uppercase',
                  color: 'var(--t-text-mute)',
                  flexShrink: 0,
                }}>{CATEGORY_LABEL[p.category]}</span>
              )}
              {p.days_to_harvest > 0 && (
                <span style={{
                  fontSize: 11, color: 'var(--t-amber)',
                  fontFamily: 'var(--t-mono)', flexShrink: 0,
                }}>{p.days_to_harvest}d</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
